import { ref, onMounted, onUnmounted } from 'vue';
import { toastController } from '@ionic/vue';

export function useFullscreen() {
  const isFullscreen = ref(false);
  const isSupported = ref(false);

  // Check if fullscreen is supported
  const checkSupport = () => {
    isSupported.value = !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
  };

  // Show toast notification
  const showToast = async (message: string, color: string = 'success') => {
    const toast = await toastController.create({
      message,
      duration: 2000,
      color,
      position: 'top',
      cssClass: 'fullscreen-toast'
    });
    await toast.present();
  };

  // Enter fullscreen
  const enterFullscreen = async () => {
    if (!isSupported.value) return;

    try {
      const element = document.documentElement;
      
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }
      
      isFullscreen.value = true;
      await showToast('Fullscreen mode activated! Press ESC to exit.', 'success');
    } catch (error) {
      console.error('Error entering fullscreen:', error);
      await showToast('Failed to enter fullscreen mode.', 'danger');
    }
  };

  // Exit fullscreen
  const exitFullscreen = async () => {
    if (!isSupported.value) return;

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      
      isFullscreen.value = false;
      await showToast('Exited fullscreen mode.', 'medium');
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
      await showToast('Failed to exit fullscreen mode.', 'danger');
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    if (isFullscreen.value) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  };

  // Handle fullscreen change events
  const handleFullscreenChange = () => {
    isFullscreen.value = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
  };

  // Handle ESC key
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isFullscreen.value) {
      exitFullscreen();
    }
  };

  onMounted(() => {
    checkSupport();
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    document.removeEventListener('keydown', handleKeyDown);
  });

  return {
    isFullscreen,
    isSupported,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen
  };
}
